import React from 'react';
import { PlaneIcon, UtensilsIcon, WineIcon } from 'lucide-react';
import { GlucoseCurve } from './GlucoseCurve';
import { PlateCode } from './PlateCode';
import type { PillarVisual } from '../../data/offer';

/** Escudo protegiendo una célula: fibra, proteína y grasa como guardaespaldas. */
function ShieldCell() {
  return <svg viewBox="0 0 220 180" className="mx-auto h-auto w-full max-w-[240px]" role="img" aria-label="Un escudo protegiendo una célula de los picos de azúcar" data-id="element-458" data-mp="17n55ix8nznuj">
      <g stroke="#d4af37" strokeWidth="2" strokeLinecap="round" data-id="element-459" data-mp="xq0funfm1a8z">
        <path d="M14 38 L44 50" data-id="element-460" data-mp="xrv4abq3bkht" />
        <path d="M10 88 L42 88" data-id="element-461" data-mp="tnvnrt4t8log" />
        <path d="M14 138 L44 126" data-id="element-462" data-mp="toyg85hs6w6h" />
      </g>
      <g fill="#d4af37" data-id="element-463" data-mp="tq02750c532z">
        <circle cx="46" cy="50" r="4" data-id="element-464" data-mp="oplslij35l2u" />
        <circle cx="44" cy="88" r="4" data-id="element-465" data-mp="oqn7xz74wtqd" />
        <circle cx="46" cy="126" r="4" data-id="element-466" data-mp="2o26d02pbhhur" />
      </g>

      <path d="M96 22 L62 36 V92 C62 126 78 146 96 158 C114 146 130 126 130 92 V36 Z" fill="#1a472a" data-id="element-467" data-mp="2o0v8u1rym834" />
      <path d="M96 34 L74 43 V92 C74 118 85 134 96 143 C107 134 118 118 118 92 V43 Z" fill="none" stroke="#d4af37" strokeWidth="2" data-id="element-468" data-mp="2j3msm6qdge4i" />

      <circle cx="176" cy="90" r="34" fill="#f0f4e8" stroke="#1a472a" strokeWidth="2" data-id="element-469" data-mp="2458xk3v7fp8e" />
      <circle cx="176" cy="90" r="12" fill="#4caf50" data-id="element-470" data-mp="28zwe9gjyvfcf" />
      <g fill="#1a472a" opacity="0.5" data-id="element-471" data-mp="1v39hbds0kp6i">
        <circle cx="160" cy="72" r="3" data-id="element-472" data-mp="1v4atjlkrnhpt" />
        <circle cx="192" cy="78" r="3" data-id="element-473" data-mp="200susbgw0xxe" />
        <circle cx="166" cy="108" r="3" data-id="element-474" data-mp="201uf76jsmwrs" />
        <circle cx="192" cy="104" r="3" data-id="element-475" data-mp="2nr5ai8idjyyj" />
      </g>
    </svg>;
}

/** Ciclo menstrual en fases + gota de vinagre. */
function CycleVinegar() {
  const phases = [{
    label: 'Folicular',
    color: '#4caf50',
    from: -90,
    to: 0
  }, {
    label: 'Ovulación',
    color: '#d4af37',
    from: 0,
    to: 60
  }, {
    label: 'Lútea',
    color: '#1a472a',
    from: 60,
    to: 180
  }, {
    label: 'Menstrual',
    color: '#a8b3a0',
    from: 180,
    to: 270
  }];
  const arc = (from: number, to: number) => {
    const r = 58;
    const cx = 80;
    const cy = 80;
    const rad = (deg: number) => deg * Math.PI / 180;
    const x1 = cx + r * Math.cos(rad(from));
    const y1 = cy + r * Math.sin(rad(from));
    const x2 = cx + r * Math.cos(rad(to));
    const y2 = cy + r * Math.sin(rad(to));
    const large = to - from > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };
  return <div className="flex flex-col items-center gap-5 sm:flex-row sm:justify-center" data-id="element-476" data-mp="36fyyyvs4qkl8">
      <svg viewBox="0 0 160 160" className="h-auto w-36 shrink-0 sm:w-40" role="img" aria-label="Diagrama del ciclo menstrual dividido en cuatro fases" data-id="element-477" data-mp="32dc5yrjq2tmg">
        {phases.map(phase => <path key={phase.label} d={arc(phase.from, phase.to)} fill="none" stroke={phase.color} strokeWidth="12" strokeLinecap="round" data-id="element-478" data-mp="2xl11rchy8lo3" />)}
        <text x="80" y="76" textAnchor="middle" fill="#1a472a" fontFamily="Inter, sans-serif" fontSize="13" fontWeight="800" data-id="element-479" data-mp="2nph9r16ia74m">
          CICLO
        </text>
        <text x="80" y="94" textAnchor="middle" fill="#666666" fontFamily="Inter, sans-serif" fontSize="11" data-id="element-480" data-mp="2irfrgigwmbu7">
          4 fases
        </text>
      </svg>

      <div className="flex min-w-0 items-center gap-3" data-id="element-481" data-mp="u4xjksoyb5ua">
        <svg viewBox="0 0 60 80" className="h-16 w-auto shrink-0 sm:h-20" role="img" aria-label="Gota de vinagre diluido en agua" data-id="element-482" data-mp="y7kc6vtg4usj">
          <path d="M30 6 C30 6 50 34 50 50 A20 20 0 0 1 10 50 C10 34 30 6 30 6 Z" fill="#d4af37" data-id="element-483" data-mp="136dt6y7xz3xg" />
          <path d="M30 20 C30 20 42 38 42 50 A12 12 0 0 1 18 50 C18 38 30 20 30 20 Z" fill="#e7d9a6" data-id="element-484" data-mp="1860779vlgo7b" />
          <path d="M14 70 H46" stroke="#1a472a" strokeWidth="3" strokeLinecap="round" data-id="element-485" data-mp="aj15ultyis33" />
        </svg>
        <p className="text-xs leading-relaxed text-muted" data-id="element-486" data-mp="fi4cgapl56d6">
          <strong className="block text-sm font-bold text-forest" data-id="element-487" data-mp="kg5vwwqxb7p6">1 cda. de vinagre</strong>
          en agua, 10 min antes
        </p>
      </div>
    </div>;
}

/** Escenarios reales: restaurante, alcohol y viajes. */
function Scenarios() {
  const items = [{
    icon: UtensilsIcon,
    label: 'Restaurante'
  }, {
    icon: WineIcon,
    label: 'Alcohol'
  }, {
    icon: PlaneIcon,
    label: 'Viajes'
  }];
  return <ul className="mx-auto grid w-full max-w-[300px] grid-cols-3 gap-2 sm:gap-3" data-id="element-488" data-mp="1yfhbixxnc71h">
      {items.map(({
      icon: Icon,
      label
    }) => <li key={label} className="flex min-w-0 flex-col items-center gap-2 rounded-xl border border-gold/50 bg-white px-2 py-4 text-center" data-id="element-489" data-mp="1ufglsh53k9f4">
          <Icon className="h-6 w-6 shrink-0 text-forest" aria-hidden="true" data-id="element-490" data-mp="23eu0bo82vo6p" />
          <span className="text-[0.7rem] font-semibold leading-tight text-ink sm:text-xs" data-id="element-491" data-mp="23cqzsa9aucnq">{label}</span>
        </li>)}
    </ul>;
}

/** Calendario de 21 días con checks verdes. */
function DayMap() {
  return <div className="mx-auto max-w-[260px]" data-id="element-492" data-mp="2d6rdh0incq82">
      <div className="grid grid-cols-7 gap-1.5" role="img" aria-label="Calendario de 21 días completados" data-id="element-493" data-mp="2r4y01ki5b3sf">
        {Array.from({
        length: 21
      }).map((_, index) => <span key={index} className={['flex aspect-square items-center justify-center rounded-md text-[0.6rem] font-bold', index < 14 ? 'bg-leaf text-white' : 'bg-cream text-forest'].join(' ')} data-id="element-494" data-mp="2m3ija4n03jfu" data-mpi={index}>
            {index < 14 ? '✓' : index + 1}
          </span>)}
      </div>
      <p className="mt-3 text-center text-xs text-muted" data-id="element-495" data-mp="33f78aojbzumm">Semana 1 · Semana 2 · Semana 3</p>
    </div>;
}
export function PillarVisual({
  visual
}: {
  visual: PillarVisual;
}) {
  switch (visual) {
    case 'curve':
      return <GlucoseCurve data-id="element-496" data-mp="2ukj471phsize" />;
    case 'shield':
      return <ShieldCell data-id="element-497" data-mp="2ymvwoof8ake8" />;
    case 'plate':
      return <PlateCode data-id="element-498" data-mp="2yluocrplkc07" />;
    case 'scenarios':
      return <Scenarios data-id="element-499" data-mp="2knwhs8hipjv9" />;
    case 'cycle':
      return <CycleVinegar data-id="element-500" data-mp="2kq91or6u64ay" />;
    case 'calendar':
      return <DayMap data-id="element-501" data-mp="2pnivsk567454" />;
    default:
      return null;
  }
}